import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, SectionLabel, probColor, SearchBar, searchMatch, ScrollRow, tierFromProb, inGameFilter } from '../ui'
import { PickCard, isStrongCase } from '../cards'
import { getNFLLeaderboard, getNFLConsensus, NFL_MARKETS } from '../api'
import { shareNflRankings } from '../share'
import { teamNick } from '../plain'
import { useOddsRange, inOddsRange, oddsRangeLabel } from '../oddsfilter'

// One row of mutually-exclusive filter chips (a labelled segmented control).
function ChipRow({ label, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {options.map(([val, lbl]) => { const on = value === val; return (
          <button key={String(val)} onClick={() => onChange(val)} style={{ padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
            fontSize: 11.5, fontWeight: 700, border: `1px solid ${on ? T.green : T.border}`,
            background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{lbl}</button>
        )})}
      </div>
    </div>
  )
}

const _hitPct = e => Math.round((e.hit_prob != null ? e.hit_prob : e.book_prob_over != null ? e.book_prob_over : e.prob_over || 0) * 100)

// Consensus play — a player the model likes across multiple markets at once. Folded
// in from the old Consensus tab; multi-market conviction is the strongest signal.
function ConsensusRow({ p, onPlayer }) {
  return (
    <button onClick={() => onPlayer && onPlayer({ full_name: p.name, pos: p.pos, team: p.team, opp: p.opp,
        // avg_prob spans multiple markets — not a single-bet probability, so don't feed
        // it to the single-market bet card (the % is shown in the row itself below).
        ...(p.gsis_id ? { gsis_id: p.gsis_id } : {}), note: `${p.count} markets · ${p.avg_prob}% avg`, status: 'active' })}
      style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', background: T.card,
        border: `1px solid ${p.count >= 3 ? T.greenMid : T.border}`, borderRadius: 10, padding: '10px 13px', marginBottom: 7, color: T.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: 14 }}>{p.name} <span style={{ color: T.muted, fontSize: 11 }}>{p.pos} · {teamNick(p.team)} vs {teamNick(p.opp)}</span></span>
        <span style={{ fontFamily: T.mono, fontWeight: 800, color: probColor((p.avg_prob || 0) / 100) }}>{p.avg_prob}%</span>
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
        {p.markets.map((mk, i) => (
          <span key={i} style={{ fontSize: 10, fontWeight: 700, color: T.green, background: T.greenDim,
            border: `1px solid ${T.greenMid}`, borderRadius: 5, padding: '2px 7px' }}>{mk}</span>
        ))}
      </div>
    </button>
  )
}

export default function PicksTab({ season, week, onPlayer, initialMarket, gameFilter }) {
  const oddsRange = useOddsRange()
  // Default to touchdowns, unless a specific market was deep-linked in (e.g. tapping the
  // "Passing" row on Home opens passing rankings).
  const [market, setMarket] = useState(
    (initialMarket && NFL_MARKETS.some(m => m.key === initialMarket)) ? initialMarket : 'anytime_td')
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [carry, setCarry] = useState(null)
  const [injOk, setInjOk] = useState(true)  // false → injury feed didn't load; warn users
  const [odds, setOdds] = useState(null)    // book-line freshness for the header stamp
  const [hasLive, setHasLive] = useState(false)   // a game is in progress → poll live progress
  const [con, setCon] = useState(null)
  const [tierRecs, setTierRecs] = useState([])      // live season hit rate per tier (board steering)
  const [strongOnly, setStrongOnly] = useState(false)
  const [convOnly, setConvOnly] = useState(false)   // only Elite / Best Bet / Strong tiers
  const [tierPick, setTierPick] = useState(null)    // tap a tier card → filter to that exact tier
  const [q, setQ] = useState('')          // player / team / opponent search
  // Full filter set
  const [showFilters, setShowFilters] = useState(false)
  const [pos, setPos] = useState('ALL')
  const [minHit, setMinHit] = useState(0)
  const [minEdge, setMinEdge] = useState(null)   // null = any; else edge_pct >=
  const [beatable, setBeatable] = useState(false)  // value: beats the posted price (+EV)
  const [priced, setPriced] = useState(false)      // has a posted book line
  const [steaming, setSteaming] = useState(false)  // market moving toward our side (live CLV)
  const [sort, setSort] = useState('rank')         // rank | hit | edge | steam | conf
  const clearFilters = () => { setPos('ALL'); setMinHit(0); setMinEdge(null); setBeatable(false); setPriced(false); setSteaming(false); setSort('rank'); setStrongOnly(false); setConvOnly(false); setTierPick(null) }
  const activeCount = (pos !== 'ALL' ? 1 : 0) + (minHit ? 1 : 0) + (minEdge != null ? 1 : 0) + (beatable ? 1 : 0) + (priced ? 1 : 0) + (steaming ? 1 : 0) + (strongOnly ? 1 : 0) + (convOnly ? 1 : 0) + (tierPick ? 1 : 0) + (sort !== 'rank' ? 1 : 0)
  const HIGH_TIERS = new Set(['ELITE', 'BEST BET', 'STRONG'])

  useEffect(() => {
    let alive = true; setLoading(true)
    getNFLLeaderboard(market, season, week).then(res => {
      if (!alive) return
      setRows(res.rows || []); setSample(!!res._sample)
      setCarry(res.carryover ? res.source_season : null)
      setTierRecs(res.tier_records || [])
      setInjOk(res.injuries_loaded !== false); setOdds(res.odds || null); setLoading(false)
      setHasLive(!!res.has_live)
    })
    return () => { alive = false }
  }, [market, season, week])

  // While a game is in progress, refresh the board every 30s so the live progress on each
  // card ticks up. Rows only (no spinner) — the card is otherwise unchanged. Stops when the
  // slate goes quiet (has_live false).
  useEffect(() => {
    if (!hasLive) return
    let alive = true
    const id = setInterval(() => {
      getNFLLeaderboard(market, season, week).then(res => {
        if (!alive) return
        setRows(res.rows || []); setHasLive(!!res.has_live)
      })
    }, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [hasLive, market, season, week])

  useEffect(() => {
    let alive = true
    getNFLConsensus(season, week).then(r => { if (alive) setCon(r?.rows || []) })
    return () => { alive = false }
  }, [season, week])

  return (
    <div style={{ padding: '14px 16px 80px' }}>
      {/* Consensus — players strong across multiple markets */}
      {con?.length > 0 && <>
        <SectionLabel>★ Consensus · strong across markets</SectionLabel>
        {con.slice(0, 4).map((p, i) => <ConsensusRow key={i} p={p} onPlayer={onPlayer} />)}
        <SectionLabel>By market</SectionLabel>
      </>}

      {/* Market pills */}
      <ScrollRow style={{ marginBottom: 6 }}>
        {NFL_MARKETS.map(m => { const on = m.key === market; return (
          <button key={m.key} onClick={() => setMarket(m.key)} style={{ flexShrink: 0, padding: "7px 13px", borderRadius: 20, cursor: "pointer",
            background: on ? T.green : T.card, color: on ? "#04140D" : T.muted, border: `1px solid ${on ? T.green : T.border}`, fontSize: 12, fontWeight: 800, letterSpacing: "0.02em" }}>
            {m.label}</button>
        )})}
      </ScrollRow>
      {/* STEER TO THE WIN — live season hit rate by conviction tier, so users bet where we actually
          beat the book (the top tiers) and skip the coin-flip leans. One tap filters to them. Our
          edge lives at the top of the funnel; showing it plainly is how the board makes users win. */}
      {tierRecs.length > 0 && rows?.length > 0 && (() => {
        const by = Object.fromEntries(tierRecs.map(t => [t.tier, t]))
        const top = ['ELITE', 'BEST BET', 'STRONG'].map(k => by[k]).filter(t => t && t.plays >= 5)
        if (!top.length) return null
        return (
          <div style={{ background: T.card, border: `1px solid ${convOnly ? T.green : T.border}`, borderRadius: 11,
            padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '0.06em', marginBottom: 7 }}>
              📊 WHERE WE WIN · season hit rate by tier</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 7 }}>
              {top.map(t => { const on = tierPick === t.tier
                return (
                <button key={t.tier} onClick={() => { setTierPick(on ? null : t.tier); setConvOnly(false) }}
                  style={{ flex: '1 1 0', minWidth: 88, textAlign: 'left', cursor: 'pointer', color: T.text,
                    background: on ? T.greenDim : (T.card2 || T.card),
                    border: `1px solid ${on ? T.green : (T.border2 || T.border)}`, borderRadius: 8, padding: '7px 9px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: on ? T.green : T.muted, letterSpacing: '0.04em' }}>{t.tier}</div>
                  <div style={{ fontFamily: T.mono, fontSize: 19, fontWeight: 900, lineHeight: 1.1,
                    color: probColor((t.hit_rate || 0) / 100) }}>{t.hit_rate}%</div>
                  <div style={{ fontSize: 10, color: T.faint, fontWeight: 600 }}>{t.wins}-{t.plays - t.wins} · {t.plays} graded</div>
                </button>
              )})}
            </div>
            <div style={{ fontSize: 10, color: T.faint, marginBottom: 8 }}>Tap a tier to show only those plays.</div>
            <button onClick={() => { setConvOnly(v => !v); setTierPick(null) }} style={{ width: '100%', padding: '8px 0', cursor: 'pointer',
              background: convOnly ? T.green : 'transparent', color: convOnly ? '#04140D' : T.green,
              border: `1px solid ${T.green}`, borderRadius: 8, fontWeight: 800, fontSize: 12 }}>
              {convOnly ? '✓ Showing all top tiers' : 'Show all top tiers →'}</button>
          </div>
        )
      })()}
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8, letterSpacing: "0.05em" }}>● Live projections unavailable right now — check back shortly</div>}
      {/* Book-line freshness — turns "why is this a model line?" into a fact: when we
          last pulled, and how many of THIS market came through the API. Green when the
          market has posted lines, amber when it's model-estimate-only. */}
      {odds && rows?.length > 0 && (() => {
        const marketLabel = (NFL_MARKETS.find(x => x.key === market) || {}).label || market
        const age = odds.age_min == null ? null
          : odds.age_min < 1 ? 'just now'
          : odds.age_min < 60 ? `${odds.age_min}m ago`
          : `${Math.floor(odds.age_min / 60)}h ${odds.age_min % 60}m ago`
        const has = odds.market_count > 0
        return (
          <div style={{ fontSize: 10.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            color: has ? T.muted2 : T.amber }}>
            <span style={{ color: has ? T.green : T.amber }}>●</span>
            {odds.pulled_at
              ? <span>Book lines pulled <b style={{ color: has ? T.text : T.amber }}>{age}</b> · {marketLabel}{' '}
                  <b style={{ color: has ? T.green : T.amber }}>{odds.market_count}</b>
                  {has ? ' posted' : ' — showing model estimates until books post them'}</span>
              : <span>No book lines yet — showing model estimates. Book lines post midweek and firm up near kickoff.</span>}
          </div>
        )
      })()}
      {carry && <div style={{ fontSize: 10.5, color: T.amber, background: T.amberDim, border: `1px solid ${T.amber}44`, borderRadius: 8, padding: "7px 10px", marginBottom: 8 }}>
        ● Preseason board — projected off <b>{carry}</b> data until Week 1 games are played. Numbers sharpen once the season starts.</div>}
      {!injOk && rows?.length > 0 && <div style={{ fontSize: 10.5, color: T.amber, background: T.amberDim, border: `1px solid ${T.amber}66`, borderRadius: 8, padding: "7px 10px", marginBottom: 8 }}>
        ⚠ Injury report not loaded for this week — <b>confirm a player is active</b> before betting. An OUT player may still appear here.</div>}

      {/* Search — filter the market by player, team, or opponent */}
      {rows?.length > 0 && <SearchBar value={q} onChange={setQ} />}

      {/* Filter bar — Strong-case quick toggle + a full Filters panel */}
      {rows?.length > 0 && (
        <ScrollRow style={{ marginBottom: 10 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* One-tap to the model's highest-conviction plays (Elite · Best Bet · Strong). */}
          <button onClick={() => setConvOnly(v => !v)}
            title="Only the model's top tiers — Elite, Best Bet, Strong"
            style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap',
              color: convOnly ? '#04140D' : T.green, background: convOnly ? T.green : T.greenDim,
              border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 11px' }}>
            {convOnly ? '✓ ' : '🔥 '}Elite / Strong
          </button>
          <button onClick={() => setStrongOnly(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap',
              color: strongOnly ? '#04140D' : T.green, background: strongOnly ? T.green : T.greenDim,
              border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 11px' }}>
            {strongOnly ? '✓ ' : ''}Strong case
          </button>
          {/* Value board: only plays that beat the posted price (+EV), best edge first.
              A value flag, not a wager-size recommendation. */}
          <button onClick={() => { const nv = !beatable; setBeatable(nv); setSort(nv ? 'edge' : 'rank') }}
            title="Only +EV plays that beat the posted price, sorted by edge"
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
              color: beatable ? '#04140D' : T.green, background: beatable ? T.green : T.greenDim,
              border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 11px' }}>
            {beatable ? '✓ ' : ''}✓ Value
          </button>
          {/* Steam: only plays the market has moved toward since the open (live CLV),
              biggest move first. Market movement, not a pick. */}
          <button onClick={() => { const nv = !steaming; setSteaming(nv); setSort(nv ? 'steam' : 'rank') }}
            title="Only plays the market has moved toward since it opened, biggest move first"
            style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap',
              color: steaming ? '#04140D' : T.green, background: steaming ? T.green : T.greenDim,
              border: `1px solid ${T.green}`, borderRadius: 8, padding: '6px 11px' }}>
            {steaming ? '✓ ' : '📈 '}Steam
          </button>
          <button onClick={() => setShowFilters(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
              color: activeCount ? T.green : T.muted, background: T.card, border: `1px solid ${activeCount ? T.green : T.border}`,
              borderRadius: 8, padding: '6px 11px' }}>
            ⚙ Filters{activeCount ? <span style={{ background: T.green, color: '#04140D', borderRadius: 10, padding: '0 6px', fontSize: 10 }}>{activeCount}</span> : null}
          </button>
          {activeCount > 0 && <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Clear</button>}
          {/* Export the current market's rankings as a clean, branded graphic to share. */}
          <button onClick={() => shareNflRankings(market, season, week)}
            title="Export this market's rankings as a shareable graphic"
            style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap',
              color: T.muted2, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 11px' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15V3M7 8l5-5 5 5M4 15v4a2 2 0 002 2h12a2 2 0 002-2v-4"/></svg>
            Export
          </button>
        </div></ScrollRow>
      )}

      {/* Full filters panel */}
      {showFilters && rows?.length > 0 && (
        <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: '12px 13px', marginBottom: 10 }}>
          <ChipRow label="POSITION" value={pos} onChange={setPos}
            options={[['ALL', 'All'], ['QB', 'QB'], ['RB', 'RB'], ['WR', 'WR'], ['TE', 'TE']]} />
          <ChipRow label="MIN HIT %" value={minHit} onChange={setMinHit}
            options={[[0, 'Any'], [55, '55+'], [60, '60+'], [65, '65+'], [70, '70+']]} />
          <ChipRow label="MIN EDGE" value={minEdge} onChange={setMinEdge}
            options={[[null, 'Any'], [0, '+EV'], [1, '+1%'], [3, '+3%'], [5, '+5%']]} />
          <ChipRow label="SORT BY" value={sort} onChange={setSort}
            options={[['rank', 'Rank'], ['hit', 'Hit %'], ['edge', 'Edge'], ['steam', 'Steam'], ['conf', 'Conf']]} />
          <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
            {[['beatable', 'Value only', beatable, setBeatable], ['priced', 'Priced only', priced, setPriced]].map(([k, lbl, val, set]) => (
              <button key={k} onClick={() => set(v => !v)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer',
                fontSize: 11.5, fontWeight: 800, border: `1px solid ${val ? T.green : T.border}`,
                background: val ? T.greenDim : T.card, color: val ? T.green : T.muted }}>{val ? '✓ ' : ''}{lbl}</button>
            ))}
          </div>
        </div>
      )}

      {/* Apply filters + sort */}
      {(() => {
        const match = searchMatch(q, e => [e.full_name, e.team, e.opp])
        // Search-only hits (before the toggle filters) — so an empty board can tell the
        // truth: "he's here but a filter hid him" vs "he's genuinely not in this market".
        const searchHits = (rows || []).filter(match)
        let shown = searchHits.filter(e => inGameFilter(gameFilter, e.team))   // global game filter (top ticker)
          .filter(e => inOddsRange(oddsRange, e.book_over_odds))   // global odds range (💲 drawer)
        const _tierOf = e => e.tier || tierFromProb((_hitPct(e) || 0) / 100, e.confidence || 0, e.edge_pct, e.prop_type)
        if (convOnly) shown = shown.filter(e => HIGH_TIERS.has(_tierOf(e)))
        if (tierPick) shown = shown.filter(e => _tierOf(e) === tierPick)   // one tier only (tapped a tier card)
        if (strongOnly) shown = shown.filter(isStrongCase)
        if (pos !== 'ALL') shown = shown.filter(e => e.pos === pos)
        if (minHit) shown = shown.filter(e => _hitPct(e) >= minHit)
        if (minEdge != null) shown = shown.filter(e => (e.edge_pct != null ? e.edge_pct : -99) >= minEdge)
        if (beatable) shown = shown.filter(e => e.value)
        if (priced) shown = shown.filter(e => e.book_line != null)
        if (steaming) shown = shown.filter(e => e.steam_pts != null && e.steam_pts >= 1.5)
        const cmp = { hit: (a, b) => _hitPct(b) - _hitPct(a), edge: (a, b) => (b.edge_pct || -99) - (a.edge_pct || -99),
          steam: (a, b) => (b.steam_pts ?? -99) - (a.steam_pts ?? -99),
          conf: (a, b) => (b.confidence || 0) - (a.confidence || 0) }
        if (cmp[sort]) shown = [...shown].sort(cmp[sort])
        // Which toggles are actively narrowing the board right now.
        const activeFilters = [convOnly && 'Elite / Strong', tierPick && tierPick, strongOnly && 'Strong case', beatable && 'Value',
          steaming && 'Steam', priced && 'Priced only', pos !== 'ALL' && pos,
          minHit && `${minHit}%+ hit`, minEdge != null && 'min edge', oddsRange && `odds ${oddsRangeLabel(oddsRange)}`].filter(Boolean)
        let emptyMsg
        if (q.trim() && searchHits.length > 0) {
          // The player IS on this market's board — a filter is hiding him, not absence.
          const n = searchHits.length
          emptyMsg = `${n} play${n > 1 ? 's' : ''} for “${q}” ${n > 1 ? 'are' : 'is'} hidden by your ${activeFilters.join(' + ')} filter${activeFilters.length > 1 ? 's' : ''}. Tap it above to see ${n > 1 ? 'them' : 'it'}.`
        } else if (q.trim()) {
          const mLabel = (NFL_MARKETS.find(x => x.key === market) || {}).label || market
          emptyMsg = `No “${q}” on the ${mLabel} board — try another market, or Players → Stats to search every player.`
        } else if (searchHits.length > 0 && activeFilters.length) {
          // Plays exist — the filters hid them. Name the culprit, and flag the one that can NEVER
          // match on this market: Value needs a two-sided line, so it kills a one-sided TD board.
          const mLabel = (NFL_MARKETS.find(x => x.key === market) || {}).label || market
          const oneSided = market === 'anytime_td' || market === 'pass_tds'
          if (beatable && oneSided) {
            const others = activeFilters.filter(f => f !== 'Value')
            emptyMsg = `The Value filter needs a two-sided price, but ${mLabel} is one-sided — no play can qualify. Turn off Value${others.length ? ` to see your ${others.join(' + ')} plays` : ''}.`
          } else {
            const n = searchHits.length
            emptyMsg = `${n} play${n > 1 ? 's' : ''} ${n > 1 ? 'are' : 'is'} hidden by your ${activeFilters.join(' + ')} filter${activeFilters.length > 1 ? 's' : ''}. Tap a filter above to loosen it, or Clear to reset.`
          }
        } else {
          emptyMsg = 'No plays match these filters — tap Clear to reset'
        }
        return (<>
          {rows?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, fontSize: 11, color: T.muted }}>
              <span>{shown.length} of {rows.length} shown</span>
              {beatable && <span style={{ color: T.green, fontWeight: 700 }}>+EV plays only</span>}
            </div>
          )}
          {loading ? <Spinner />
            : !rows?.length ? <Empty message="No projections for this week yet" />
            : !shown.length ? <Empty message={emptyMsg} />
            : shown.map((e, i) => <PickCard key={i} e={e} rank={sort === 'rank' ? (e.rank || i + 1) : i + 1} onOpen={onPlayer} carrySeason={carry} />)}
        </>)
      })()}
    </div>
  )
}
